import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';
import { API_BASE, UPLOAD_BASE } from '../config/apiConfig';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('date-desc');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions');

  // Property data import state
  const [importCounties, setImportCounties] = useState([]);
  const [selectedState, setSelectedState] = useState('Indiana');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [importStatus, setImportStatus] = useState(null); // null | 'running' | 'done' | 'error'
  const [importRows, setImportRows] = useState(0);
  const [importMessage, setImportMessage] = useState('');

  useEffect(() => {
    fetchSubmissions();
    // Refresh every 1 second to get latest statuses (Note that this is only for development purposes)
    const interval = setInterval(fetchSubmissions, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared SSE stream reader for property import
  const streamImport = (fetchPromise, countyLabel) => {
    setImportStatus('running');
    setImportRows(0);
    setImportMessage('');
    fetchPromise.then(response => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const pump = () => reader.read().then(({ done, value }) => {
        if (done) return;
        decoder.decode(value).split('\n').forEach(line => {
          if (!line.startsWith('data:')) return;
          try {
            const msg = JSON.parse(line.slice(5));
            if (msg.type === 'progress') setImportRows(msg.rows);
            if (msg.type === 'done') {
              setImportStatus('done');
              setImportMessage(`Successfully imported ${msg.total.toLocaleString()} rows for ${countyLabel} County.`);
              axios.get(`${API_BASE}/admin/property-import/counties`, { withCredentials: true })
                .then(r => setImportCounties(r.data.counties || []));
            }
            if (msg.type === 'error') {
              setImportStatus('error');
              setImportMessage(`Error: ${msg.message}`);
            }
          } catch {}
        });
        pump();
      });
      pump();
    }).catch(err => {
      setImportStatus('error');
      setImportMessage(`Error: ${err.message}`);
    });
  };

  // Load available county CSVs when Data tab is opened
  useEffect(() => {
    if (activeTab !== 'data') return;
    axios.get(`${API_BASE}/admin/property-import/counties`, { withCredentials: true })
      .then(res => setImportCounties(res.data.counties || []))
      .catch(() => setImportCounties([]));
  }, [activeTab]);

  // Filter submissions - exclude auto-rejected (red AI status) from admin view
  useEffect(() => {
    // Filter out auto-rejected submissions (red AI status) - they don't reach admin
    let filtered = submissions.filter(sub => {
      // Exclude submissions that were auto-rejected by AI (red status)
      if (sub.aiStatus === 'red' && sub.status === 'rejected') {
        return false;
      }
      return true;
    });

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sub => {
        // Search by Submission ID
        if (sub.id.toLowerCase().includes(query)) return true;
        
        // Search by Tyler ID
        if (sub.tylerId && sub.tylerId.toLowerCase().includes(query)) return true;
        
        // Search by Status
        if (getStatusLabel(sub.status).toLowerCase().includes(query)) return true;
        
        // Search by form data fields (if available)
        if (sub.data) {
          const data = sub.data;
          // Owner name
          if (data.ownerName && data.ownerName.toLowerCase().includes(query)) return true;
          // Owner email
          if (data.ownerEmail && data.ownerEmail.toLowerCase().includes(query)) return true;
          // Street address
          if (data.streetAddress && data.streetAddress.toLowerCase().includes(query)) return true;
          // City
          if (data.city && data.city.toLowerCase().includes(query)) return true;
          // Contractor name
          if (data.contractorName && data.contractorName.toLowerCase().includes(query)) return true;
          // Business name
          if (data.businessName && data.businessName.toLowerCase().includes(query)) return true;
        }
        
        return false;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'rejected') {
        filtered = filtered.filter(sub => sub.isRejected);
      } else {
        filtered = filtered.filter(sub => sub.status === statusFilter && !sub.isRejected);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'az':
          return a.id.localeCompare(b.id);
        case 'za':
          return b.id.localeCompare(a.id);
        case 'date-desc':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'date-asc':
          return new Date(a.timestamp) - new Date(b.timestamp);
        default:
          return new Date(b.timestamp) - new Date(a.timestamp);
      }
    });

    setFilteredSubmissions(filtered);
  }, [submissions, searchQuery, statusFilter, sortOrder]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-wrapper')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  const getJurisdictionParams = () => {
    try {
      const raw = localStorage.getItem('selectedCounty');
      if (!raw) return {};
      const loc = JSON.parse(raw);
      const params = new URLSearchParams();
      if (loc.county) params.set('county', loc.county);
      if (loc.city) params.set('city', loc.city);
      return params;
    } catch {
      return {};
    }
  };

  const fetchSubmissions = async () => {
    try {
      const params = getJurisdictionParams();
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API_BASE}/admin/submissions${query}`);
      const submissionsList = response.data.submissions || [];
      // Sort by timestamp descending (newest first)
      const sortedSubmissions = submissionsList.sort((a, b) => {
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
      setSubmissions(sortedSubmissions);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      setLoading(false);
    }
  };

  const handleViewDetails = async (submissionId) => {
    // If clicking the same submission, deselect it (close review panel)
    if (selectedSubmission && selectedSubmission.id === submissionId) {
      setSelectedSubmission(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE}/admin/submissions/${submissionId}`);
      setSelectedSubmission(response.data.submission);
    } catch (error) {
      alert('Failed to load submission details: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleApprove = async (submissionId) => {
    if (!window.confirm('Are you sure you want to approve this permit application?')) {
      return;
    }

    setActionLoading(submissionId);
    try {
      const params = Object.fromEntries(getJurisdictionParams());
      await axios.post(`${API_BASE}/admin/submissions/${submissionId}/approve`, {}, { params });
      await fetchSubmissions();
      if (selectedSubmission && selectedSubmission.id === submissionId) {
        await handleViewDetails(submissionId);
      }
      alert('Permit approved successfully!');
    } catch (error) {
      alert('Failed to approve: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (submissionId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setActionLoading(submissionId);
    try {
      const params = Object.fromEntries(getJurisdictionParams());
      await axios.post(
        `${API_BASE}/admin/submissions/${submissionId}/reject`,
        { rejectionReason: rejectionReason.trim() },
        { params }
      );
      await fetchSubmissions();
      if (selectedSubmission && selectedSubmission.id === submissionId) {
        await handleViewDetails(submissionId);
      }
      setShowRejectModal(false);
      setRejectionReason('');
      alert('Permit rejected successfully!');
    } catch (error) {
      alert('Failed to reject: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (submissionId) => {
    setShowRejectModal(submissionId);
    setRejectionReason('');
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectionReason('');
  };

  const getStatusColor = (status, isRejected) => {
    if (isRejected) return '#dc3545';
    const colors = {
      submitted: '#1976d2',
      under_review: '#f57c00',
      department_coordination: '#7b1fa2',
      decision: '#388e3c',
      issued: '#2e7d32',
      ai_approved: '#2e7d32', // Green - passed AI screening
      needs_human_review: '#f57c00', // Yellow - needs thorough review
      error: '#ff9800' // Orange - AI error
    };
    return colors[status] || '#666';
  };

  const getStatusLabel = (status) => {
    const labels = {
      ai_approved: 'AI Approved',
      needs_human_review: 'Needs Human Review',
      error: 'AI Error'
    };
    return labels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const canApprove = (status) => {
    return status !== 'issued' && status !== 'rejected';
  };

  const canReject = (status) => {
    return status !== 'issued' && status !== 'rejected';
  };

  const getNextStatus = (currentStatus) => {
    const progression = {
      'submitted': 'Under Review',
      'under_review': 'Department Coordination',
      'department_coordination': 'Decision',
      'decision': 'Issued'
    };
    return progression[currentStatus] || 'Final';
  };

  const pendingSubmissions = submissions.filter(sub => 
    !sub.isRejected && sub.status !== 'issued'
  );

  return (
    <div className="admin-dashboard-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Review and manage permit applications</p>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-number">{pendingSubmissions.length}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{submissions.filter(s => s.status === 'issued').length}</div>
          <div className="stat-label">Issued</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{submissions.filter(s => s.isRejected).length}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{submissions.length}</div>
          <div className="stat-label">Total Applications</div>
        </div>
      </div>

      <div className={`admin-grid ${selectedSubmission ? 'with-review' : 'no-review'}`}>
        {/* Submissions List */}
        <div className="admin-list-container">
          {/* Top form header */}
          <div className="dashboard-topbar">
            <div className="dashboard-form-meta">
              <div className="dashboard-form-title">Admin Dashboard</div>
              <div className="dashboard-form-subtitle">
                {filteredSubmissions.length} of {submissions.length} applications · {pendingSubmissions.length} pending review
              </div>
            </div>
          </div>

          {/* Tab navigation */}
          <div className="dashboard-tabs">
            <button className={`dashboard-tab${activeTab === 'submissions' ? ' active' : ''}`} onClick={() => setActiveTab('submissions')}>Submissions</button>
            <button className={`dashboard-tab${activeTab === 'data' ? ' active' : ''}`} onClick={() => setActiveTab('data')}>Data</button>
            <button className={`dashboard-tab${activeTab === 'analytics' ? ' active' : ''}`} onClick={() => setActiveTab('analytics')}>Analytics</button>
            <button className={`dashboard-tab${activeTab === 'settings' ? ' active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
          </div>

          {/* Data import tab */}
          {activeTab === 'data' && (
              <div className="data-import-panel">
                <h3 className="data-import-title">Property Data Import</h3>

                {/* ── Re-import existing CSV already on server ── */}
                <div className="data-import-section">
                  <div className="data-import-section-header">
                    <span className="data-import-section-label">Re-import existing</span>
                    <span className="data-import-section-desc">Re-run the import for a county whose CSV is already on the server (e.g. after a schema change).</span>
                  </div>
                  <div className="data-import-form">
                    <div className="data-import-row">
                      <label className="data-import-label">State</label>
                      <select
                        className="data-import-select"
                        value={selectedState}
                        onChange={e => { setSelectedState(e.target.value); setSelectedCounty(''); }}
                      >
                        <option value="Indiana">Indiana</option>
                      </select>
                    </div>
                    <div className="data-import-row">
                      <label className="data-import-label">County</label>
                      <select
                        className="data-import-select"
                        value={selectedCounty}
                        onChange={e => setSelectedCounty(e.target.value)}
                        disabled={importCounties.length === 0}
                      >
                        <option value="">— Select county —</option>
                        {importCounties.map(c => (
                          <option key={c.county} value={c.county}>{c.county}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="data-import-btn"
                      disabled={!selectedCounty || importStatus === 'running'}
                      onClick={() => {
                        streamImport(
                          fetch(`${API_BASE}/admin/property-import/run`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ county: selectedCounty }),
                          }),
                          selectedCounty
                        );
                      }}
                    >
                      {importStatus === 'running' ? `Importing… ${importRows.toLocaleString()} rows` : 'Import / Refresh'}
                    </button>
                  </div>
                </div>

                {importStatus === 'done' && (
                  <div className="data-import-result success">{importMessage}</div>
                )}
                {importStatus === 'error' && (
                  <div className="data-import-result error">{importMessage}</div>
                )}

                <div className="data-import-counties-loaded">
                  <h4>County files on server</h4>
                  {importCounties.length === 0 ? (
                    <p className="empty-state">No CSV files found in appeal_data/</p>
                  ) : (
                    <ul className="county-file-list">
                      {importCounties.map(c => (
                        <li key={c.county}>
                          <span className="county-file-name">{c.county}</span>
                          <span className="county-file-path">{c.filename}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
          )}

          {/* Toolbar + submissions list (submissions tab only) */}
          {activeTab === 'submissions' && (<>
          <div className="dashboard-toolbar">
            <div className="toolbar-left">
              <span className="date-label">Today</span>
              <div className="search-wrapper">
                <span className="search-icon" aria-hidden="true"></span>
                <input 
                  type="text" 
                  className="search-input" 
                  placeholder="Q Search.."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="toolbar-right">
              <div className="filter-wrapper">
                <button 
                  className="filter-button"
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <span>Filters</span>
                  {statusFilter !== 'all' && <span className="filter-badge">{statusFilter === 'rejected' ? 'Rejected' : getStatusLabel(statusFilter)}</span>}
                </button>
                {showFilterDropdown && (
                  <div className="filter-dropdown">
                    <div className="filter-option" onClick={() => { setStatusFilter('all'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'all'} readOnly />
                      <label>All</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('submitted'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'submitted'} readOnly />
                      <label>Submitted</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('under_review'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'under_review'} readOnly />
                      <label>Under Review</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('department_coordination'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'department_coordination'} readOnly />
                      <label>Department Coordination</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('decision'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'decision'} readOnly />
                      <label>Decision</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('issued'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'issued'} readOnly />
                      <label>Issued</label>
                    </div>
                    <div className="filter-option" onClick={() => { setStatusFilter('rejected'); setShowFilterDropdown(false); }}>
                      <input type="radio" checked={statusFilter === 'rejected'} readOnly />
                      <label>Rejected</label>
                    </div>
                  </div>
                )}
              </div>
              <div className="sort-wrapper">
                <button 
                  className="sort-button"
                  onClick={() => {
                    const orders = ['date-desc', 'date-asc', 'az', 'za'];
                    const currentIndex = orders.indexOf(sortOrder);
                    setSortOrder(orders[(currentIndex + 1) % orders.length]);
                  }}
                >
                  <span>
                    {sortOrder === 'az' && 'Sort by A-Z'}
                    {sortOrder === 'za' && 'Sort by Z-A'}
                    {sortOrder === 'date-desc' && 'Sort by Date (Newest)'}
                    {sortOrder === 'date-asc' && 'Sort by Date (Oldest)'}
                  </span>
                  <span className="sort-icon">⇅</span>
                </button>
              </div>
              <button 
                className={`view-toggle-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                title={viewMode === 'list' ? 'Switch to Grid View' : 'Switch to List View'}
              >
                {viewMode === 'list' ? (
                  <span className="grid-icon">⊞</span>
                ) : (
                  <span className="list-icon">≡</span>
                )}
              </button>
            </div>
          </div>

          {/* Submissions list */}
          <div className="dashboard-list-card">
            {loading ? (
              <p className="loading-state">Loading...</p>
            ) : submissions.length === 0 ? (
              <p className="empty-state">No applications submitted yet.</p>
            ) : filteredSubmissions.length === 0 ? (
              <p className="empty-state">No applications match your search criteria.</p>
            ) : viewMode === 'list' ? (
              <div className="submissions-list">
                <div className="submissions-list-header">
                  <div className="list-col name">Form name</div>
                  <div className="list-col status">Status</div>
                  <div className="list-col tyler">Tyler ID</div>
                  <div className="list-col date">Submitted</div>
                </div>
                <div className="submissions-list-body">
                  {filteredSubmissions.map((sub) => {
                    const statusColor = getStatusColor(sub.status, sub.isRejected);
                    const shortId = sub.id.substring(0, 8);
                    const initial = shortId[0]?.toUpperCase() || 'A';
                    return (
                      <button
                        key={sub.id}
                        className={`list-row ${selectedSubmission?.id === sub.id ? 'selected' : ''}`}
                        onClick={() => handleViewDetails(sub.id)}
                      >
                        <div className="list-cell name">
                          <div
                            className="list-icon"
                            style={{ backgroundColor: `${statusColor}22` }}
                          >
                            <span>{initial}</span>
                          </div>
                          <div className="list-text">
                            <div className="list-title">Submission {shortId}...</div>
                            <div className="list-subtitle">
                              {sub.isRejected
                                ? sub.rejectionReason || 'Rejected'
                                : getStatusLabel(sub.status)}
                            </div>
                          </div>
                        </div>
                        <div className="list-cell status">
                          <span
                            className="status-pill-table"
                            style={{ backgroundColor: statusColor }}
                          >
                            {getStatusLabel(sub.status)}
                          </span>
                        </div>
                        <div className="list-cell tyler">
                          {sub.tylerId || <span className="pending">Pending</span>}
                        </div>
                        <div className="list-cell date">
                          {new Date(sub.timestamp).toLocaleString()}
                        </div>
                      </button>
                );
              })}
            </div>
          </div>
            ) : (
              <div className="submissions-grid">
                {filteredSubmissions.map((sub) => {
                  const statusColor = getStatusColor(sub.status, sub.isRejected);
                  const shortId = sub.id.substring(0, 8);
                  const initial = shortId[0]?.toUpperCase() || 'A';
                  return (
                    <div
                      key={sub.id}
                      className={`grid-card ${selectedSubmission?.id === sub.id ? 'selected' : ''}`}
                      onClick={() => handleViewDetails(sub.id)}
                    >
                      <div className="grid-card-header">
                        <div
                          className="grid-icon-large"
                          style={{ backgroundColor: `${statusColor}22` }}
                        >
                          <span>{initial}</span>
                        </div>
                        <span
                          className="status-pill-grid"
                          style={{ backgroundColor: statusColor }}
                        >
                          {getStatusLabel(sub.status)}
                        </span>
                      </div>
                      <div className="grid-card-body">
                        <div className="grid-card-title">Submission {shortId}...</div>
                        <div className="grid-card-subtitle">
                          {sub.isRejected
                            ? sub.rejectionReason || 'Rejected'
                            : getStatusLabel(sub.status)}
                        </div>
                        <div className="grid-card-meta">
                          <div className="grid-meta-item">
                            <span className="grid-meta-label">Tyler ID:</span>
                            <span className="grid-meta-value">
                              {sub.tylerId || 'Pending'}
                            </span>
                          </div>
                          <div className="grid-meta-item">
                            <span className="grid-meta-label">Submitted:</span>
                            <span className="grid-meta-value">
                              {new Date(sub.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </>)}
        </div>

        {/* Review Panel - Only show when a submission is selected */}
        {selectedSubmission && (
          <div className="admin-card">
          <h2>Review Application</h2>
          {selectedSubmission ? (
            <div className="review-panel">
              <div className="review-header">
                <div>
                  <p><strong>Submission ID:</strong> {selectedSubmission.id}</p>
                  <p><strong>Tyler ID:</strong> {selectedSubmission.tylerId || 'Pending'}</p>
                  <p><strong>Status:</strong> 
                    <span 
                      className="status-badge inline"
                      style={{ backgroundColor: getStatusColor(selectedSubmission.status, selectedSubmission.isRejected) }}
                    >
                      {getStatusLabel(selectedSubmission.status)}
                    </span>
                  </p>
                  <p><strong>Submitted:</strong> {new Date(selectedSubmission.timestamp).toLocaleString()}</p>
                </div>
              </div>

              {selectedSubmission.isRejected && (
                <div className="rejection-notice">
                  <h3>Rejection Reason</h3>
                  <p>{selectedSubmission.rejectionReason}</p>
                </div>
              )}

              {/* AI Screening Results */}
              {selectedSubmission.aiStatus && (
                <div className={`ai-screening-results ai-${selectedSubmission.aiStatus}`}>
                  <h3>
                    {selectedSubmission.aiStatus === 'green' && 'AI Screening: PASSED'}
                    {selectedSubmission.aiStatus === 'yellow' && 'AI Screening: NEEDS REVIEW'}
                    {selectedSubmission.aiStatus === 'red' && 'AI Screening: FAILED'}
                    {selectedSubmission.aiStatus === 'error' && 'AI Screening: ERROR'}
                  </h3>
                  <div className="ai-reasoning">
                    <strong>AI Reasoning:</strong>
                    <p>{selectedSubmission.aiReasoning}</p>
                  </div>
                  {selectedSubmission.aiFindings && selectedSubmission.aiFindings.length > 0 && (
                    <div className="ai-findings">
                      <strong>Findings:</strong>
                      <ul>
                        {selectedSubmission.aiFindings.map((finding, idx) => (
                          <li key={idx} className={`finding-${finding.severity}`}>
                            <strong>{finding.category}:</strong> {finding.description}
                            {finding.document && <span className="finding-doc"> (from {finding.document})</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedSubmission.aiScreenedAt && (
                    <div className="ai-timestamp">
                      Screened: {new Date(selectedSubmission.aiScreenedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              )}

              <div className="application-form-view">
                <h3>Improvement Location Permit Application</h3>
                {selectedSubmission.originalData && (
                  <>
                    {/* PROJECT LOCATION */}
                    <div className="form-section-view">
                      <h4>PROJECT LOCATION</h4>
                      <div className="form-grid-view">
                        <div className="form-field-view full-width">
                          <label>Address of Construction Site</label>
                          <div className="field-value">{selectedSubmission.originalData.streetAddress || 'N/A'}</div>
                        </div>
                        {selectedSubmission.originalData.addressLine2 && (
                          <div className="form-field-view full-width">
                            <label>Address Line 2</label>
                            <div className="field-value">{selectedSubmission.originalData.addressLine2}</div>
                          </div>
                        )}
                        <div className="form-field-view">
                          <label>City</label>
                          <div className="field-value">{selectedSubmission.originalData.city || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>State</label>
                          <div className="field-value">{selectedSubmission.originalData.state || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>ZIP Code</label>
                          <div className="field-value">{selectedSubmission.originalData.zipCode || 'N/A'}</div>
                        </div>
                        {selectedSubmission.originalData.businessName && (
                          <div className="form-field-view">
                            <label>Name of Business</label>
                            <div className="field-value">{selectedSubmission.originalData.businessName}</div>
                          </div>
                        )}
                        {selectedSubmission.originalData.lotNumber && (
                          <div className="form-field-view">
                            <label>Lot Number</label>
                            <div className="field-value">{selectedSubmission.originalData.lotNumber}</div>
                          </div>
                        )}
                        {selectedSubmission.originalData.subdivision && (
                          <div className="form-field-view">
                            <label>Subdivision</label>
                            <div className="field-value">{selectedSubmission.originalData.subdivision}</div>
                          </div>
                        )}
                        {selectedSubmission.originalData.parcelNumber && (
                          <div className="form-field-view">
                            <label>Parcel Number</label>
                            <div className="field-value">{selectedSubmission.originalData.parcelNumber}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* OWNER */}
                    <div className="form-section-view">
                      <h4>OWNER</h4>
                      <div className="form-grid-view">
                        <div className="form-field-view">
                          <label>Owner Name</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerName || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Owner Phone</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerPhone || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Owner Email</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerEmail || 'N/A'}</div>
                        </div>
                        <div className="form-field-view full-width">
                          <label>Owner Street Address</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerStreet || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Owner City</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerCity || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Owner State</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerState || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Owner ZIP</label>
                          <div className="field-value">{selectedSubmission.originalData.ownerZip || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* CONTACT/CONTRACTOR */}
                    <div className="form-section-view">
                      <h4>CONTACT/CONTRACTOR</h4>
                      <div className="form-grid-view">
                        <div className="form-field-view">
                          <label>Contact/Contractor Name</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorName || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Contact/Contractor Phone</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorPhone || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Contact/Contractor Email</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorEmail || 'N/A'}</div>
                        </div>
                        <div className="form-field-view full-width">
                          <label>Contact/Contractor Street Address</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorStreet || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Contact/Contractor City</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorCity || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Contact/Contractor State</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorState || 'N/A'}</div>
                        </div>
                        <div className="form-field-view">
                          <label>Contact/Contractor ZIP</label>
                          <div className="field-value">{selectedSubmission.originalData.contractorZip || 'N/A'}</div>
                        </div>
                        {selectedSubmission.originalData.contractorLicense && (
                          <div className="form-field-view">
                            <label>Contractor License #</label>
                            <div className="field-value">{selectedSubmission.originalData.contractorLicense}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* A. Type of Primary Use */}
                    {selectedSubmission.originalData.primaryUseType && (
                      <div className="form-section-view">
                        <h4>A. Type of Primary Use</h4>
                        <div className="form-grid-view">
                          <div className="form-field-view">
                            <label>Primary Use Type</label>
                            <div className="field-value">
                              {selectedSubmission.originalData.primaryUseType
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          </div>
                          {selectedSubmission.originalData.multiFamilyUnits && (
                            <div className="form-field-view">
                              <label>Number of Units</label>
                              <div className="field-value">{selectedSubmission.originalData.multiFamilyUnits}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.primaryUseOther && (
                            <div className="form-field-view full-width">
                              <label>Other (PDMX)</label>
                              <div className="field-value">{selectedSubmission.originalData.primaryUseOther}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* B. Type of Construction */}
                    {selectedSubmission.originalData.constructionType && (
                      <div className="form-section-view">
                        <h4>B. Type of Construction</h4>
                        <div className="form-grid-view">
                          <div className="form-field-view">
                            <label>Construction Type</label>
                            <div className="field-value">
                              {selectedSubmission.originalData.constructionType
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          </div>
                          {selectedSubmission.originalData.electricalUpgradeFrom && (
                            <>
                              <div className="form-field-view">
                                <label>Electrical Upgrade From (Amps)</label>
                                <div className="field-value">{selectedSubmission.originalData.electricalUpgradeFrom}</div>
                              </div>
                              <div className="form-field-view">
                                <label>Electrical Upgrade To (Amps)</label>
                                <div className="field-value">{selectedSubmission.originalData.electricalUpgradeTo}</div>
                              </div>
                            </>
                          )}
                          {selectedSubmission.originalData.demolitionUnits && (
                            <div className="form-field-view">
                              <label>Demolition Units</label>
                              <div className="field-value">{selectedSubmission.originalData.demolitionUnits}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.constructionTypeOther && (
                            <div className="form-field-view full-width">
                              <label>Other Specification</label>
                              <div className="field-value">{selectedSubmission.originalData.constructionTypeOther}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* C. Description of Work */}
                    {selectedSubmission.originalData.description && (
                      <div className="form-section-view">
                        <h4>C. Description of Work</h4>
                        <div className="form-field-view full-width">
                          <div className="field-value">{selectedSubmission.originalData.description}</div>
                        </div>
                      </div>
                    )}

                    {/* D. Square Footage */}
                    {selectedSubmission.originalData.squareFootage && (
                      <div className="form-section-view">
                        <h4>D. Square Footage of Project</h4>
                        <div className="form-field-view">
                          <div className="field-value">{selectedSubmission.originalData.squareFootage} sq ft</div>
                        </div>
                      </div>
                    )}

                    {/* E. Proposed Building Setbacks */}
                    {(selectedSubmission.originalData.setbackFront || selectedSubmission.originalData.setbackSide1 || 
                      selectedSubmission.originalData.setbackSide2 || selectedSubmission.originalData.setbackRear) && (
                      <div className="form-section-view">
                        <h4>E. Proposed Building Setbacks</h4>
                        <div className="form-grid-view">
                          {selectedSubmission.originalData.setbackFront && (
                            <div className="form-field-view">
                              <label>Front</label>
                              <div className="field-value">{selectedSubmission.originalData.setbackFront}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.setbackSide1 && (
                            <div className="form-field-view">
                              <label>Side 1</label>
                              <div className="field-value">{selectedSubmission.originalData.setbackSide1}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.setbackSide2 && (
                            <div className="form-field-view">
                              <label>Side 2</label>
                              <div className="field-value">{selectedSubmission.originalData.setbackSide2}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.setbackRear && (
                            <div className="form-field-view">
                              <label>Rear</label>
                              <div className="field-value">{selectedSubmission.originalData.setbackRear}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* F. Estimated Cost */}
                    {selectedSubmission.originalData.projectValue && (
                      <div className="form-section-view">
                        <h4>F. Estimated Cost of Construction</h4>
                        <div className="form-field-view">
                          <div className="field-value">
                            ${parseFloat(selectedSubmission.originalData.projectValue).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* G. Historical District */}
                    {selectedSubmission.originalData.historicalDistrict && (
                      <div className="form-section-view">
                        <h4>G. Historical District</h4>
                        <div className="form-field-view">
                          <div className="field-value">
                            {selectedSubmission.originalData.historicalDistrict === 'yes' ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* H. Special Flood Hazard Area */}
                    {selectedSubmission.originalData.specialFloodHazardArea && (
                      <div className="form-section-view">
                        <h4>H. Special Flood Hazard Area</h4>
                        <div className="form-field-view">
                          <div className="field-value">
                            {selectedSubmission.originalData.specialFloodHazardArea === 'yes' ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* I. Energy Code */}
                    {(selectedSubmission.originalData.energyCodePerformance || 
                      selectedSubmission.originalData.energyCodePrescriptive || 
                      selectedSubmission.originalData.energyCodeUA) && (
                      <div className="form-section-view">
                        <h4>I. Energy Code</h4>
                        <div className="form-grid-view">
                          {selectedSubmission.originalData.energyCodePerformance && (
                            <div className="form-field-view">
                              <label>Performance</label>
                              <div className="field-value">{selectedSubmission.originalData.energyCodePerformance}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.energyCodePrescriptive && (
                            <div className="form-field-view">
                              <label>Prescriptive</label>
                              <div className="field-value">{selectedSubmission.originalData.energyCodePrescriptive}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.energyCodeUA && (
                            <div className="form-field-view">
                              <label>UA</label>
                              <div className="field-value">{selectedSubmission.originalData.energyCodeUA}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Fire Safety Notification */}
                    {(selectedSubmission.originalData.fireSafetyStreetAddress || 
                      selectedSubmission.originalData.fireSafetyTownshipCounty ||
                      selectedSubmission.originalData.fireSafetyComponentTypes ||
                      selectedSubmission.originalData.fireSafetyLocation) && (
                      <div className="form-section-view">
                        <h4>Fire Safety Notification</h4>
                        <div className="form-grid-view">
                          {selectedSubmission.originalData.fireSafetyStreetAddress && (
                            <div className="form-field-view full-width">
                              <label>Street Address</label>
                              <div className="field-value">{selectedSubmission.originalData.fireSafetyStreetAddress}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.fireSafetyTownshipCounty && (
                            <div className="form-field-view">
                              <label>Township/County</label>
                              <div className="field-value">{selectedSubmission.originalData.fireSafetyTownshipCounty}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.fireSafetyComponentTypes && (
                            <div className="form-field-view full-width">
                              <label>Types of Advanced Structural Components Used</label>
                              <div className="field-value">{selectedSubmission.originalData.fireSafetyComponentTypes}</div>
                            </div>
                          )}
                          {selectedSubmission.originalData.fireSafetyLocation && (
                            <div className="form-field-view full-width">
                              <label>Location in Structure</label>
                              <div className="field-value">{selectedSubmission.originalData.fireSafetyLocation}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Additional Notes */}
                    {selectedSubmission.originalData.notes && (
                      <div className="form-section-view">
                        <h4>Additional Notes</h4>
                        <div className="form-field-view full-width">
                          <div className="field-value">{selectedSubmission.originalData.notes}</div>
                        </div>
                      </div>
                    )}

                    {/* Attachments */}
                    {Array.isArray(selectedSubmission.originalData.attachments) &&
                      selectedSubmission.originalData.attachments.length > 0 && (
                      <div className="form-section-view">
                        <h4>Attachments (Bond, Site Plan, Supporting Documents)</h4>
                        <ul className="attachments-list">
                          {selectedSubmission.originalData.attachments.map((file, index) => (
                            <li key={index} className="attachment-item">
                              <a
                                href={`${UPLOAD_BASE}${file.url || (file.filename ? `/uploads/${file.filename}` : '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="attachment-link"
                              >
                                {file.originalName || file.filename}
                              </a>
                              {file.size != null && (
                                <span className="attachment-meta">
                                  {(file.size / 1024).toFixed(1)} KB
                                </span>
                              )}
                              {file.mimeType && (
                                <span className="attachment-meta">
                                  {file.mimeType}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="review-actions">
                {canApprove(selectedSubmission.status) && (
                  <button
                    onClick={() => handleApprove(selectedSubmission.id)}
                    disabled={actionLoading === selectedSubmission.id}
                    className="action-button approve"
                  >
                    {actionLoading === selectedSubmission.id ? 'Processing...' : `Approve → ${getNextStatus(selectedSubmission.status)}`}
                  </button>
                )}
                {canReject(selectedSubmission.status) && (
                  <button
                    onClick={() => openRejectModal(selectedSubmission.id)}
                    disabled={actionLoading === selectedSubmission.id}
                    className="action-button reject"
                  >
                    Reject Application
                  </button>
                )}
                {selectedSubmission.status === 'issued' && (
                  <div className="issued-badge">Permit Already Issued</div>
                )}
                {selectedSubmission.isRejected && (
                  <div className="rejected-badge">Application Rejected</div>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>Select an application from the list to review</p>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={closeRejectModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reject Application</h2>
            <p>Please provide a reason for rejection. This will be visible to the applicant.</p>
            <textarea
              className="rejection-textarea"
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={6}
            />
            <div className="modal-actions">
              <button onClick={closeRejectModal} className="button-secondary">
                Cancel
              </button>
              <button 
                onClick={() => handleReject(showRejectModal)} 
                className="button-danger"
                disabled={!rejectionReason.trim() || actionLoading === showRejectModal}
              >
                {actionLoading === showRejectModal ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

